//
// Genesys Tour JS
// --------------------------------------------------
// This file runs the front-end show. The most important
// function in here is goToStop(), which is used for
// most navigation-related functionality.
//
// Also, a system relying on the URL hash has been
// employed to get the built-in browser benefits
// of back/forward capability and to have a theoretical
// source of truth (the URL, though from a code standpoint
// I'm not sure it's really coded as the source of truth).
// It also allows the client to link to the tour at
// a specific Stop and have that Stop be the one that loads.
// --------------------------------------------------


// Imports
// ---------------------------------------
import { createPopper } from '@popperjs/core';
import { debounce, getPreviousSiblings } from './helpers.js';


// Variables
// ---------------------------------------
const root = document.documentElement;
const mobileMaxWidthBp = "767px";

const windowParent = window.parent;
const tourElement = document.querySelector("#gctour");
const tourStopsWrapper = document.querySelector('#gctour-stops-wrapper');
const tourStops = document.querySelectorAll('.gctour-stop-wrapper');
const firstStop = document.querySelector('#gctour-stop-1');
const welcomeStop = document.querySelector('#welcome');
const welcomePopover = document.querySelector('#popover-welcome');
const exploreStop = document.querySelector('#explore');
const endStop = document.querySelector('#end');
const endPopover = document.querySelector('#popover-end');
const hotspotsWrapper = document.querySelector('#gctour-hotspots-wrapper');
const hotspots = document.querySelectorAll('.gctour-hotspot');
const popovers = document.querySelectorAll('.popover');

const universalBar = document.querySelector("#gctour-universal-bar");
const exploreTextWrapper = document.querySelector('#ub-explore');
const crumbsWrapper = document.querySelector('#gctour-stop_crumbs-wrapper');
const crumbs = document.querySelectorAll('.gctour-stop_crumb');
const stopsWrapper = document.querySelector('#gctour-stop-1');
const tourUiBackdrop = document.querySelector('#gctour-ui-backdrop');

let initialStopID = tourElement.getAttribute("data-initial-stop");
let initialStopHash = !initialStopID || !initialStopID.length ? "#welcome" : "#" + initialStopID;
let popperInstance = null;

const popperPositioning = {
  'top-left': {
    'placement': 'left-end',
    'offset': [-18, -14]
  },
  'top-right': {
    'placement': 'right-end',
    'offset': [-18, -14]
  },
  'bottom-right': {
    'placement': 'right-start',
    'offset': [18, -14]
  },
  'bottom-left': {
    'placement': 'left-start',
    'offset': [18, -14]
  }
};

// Functions
// ---------------------------------------

/**
 * Creates a Popper instance. Adheres to Popper.js' method/documentation
 * @param {HTMLElement} hotspot - The hotspot element to hook the popper to
 * @param {HTMLElement} popover - The popover element to show
 * * @param {String} positioning - Chosen preset to attept to position to. Options are 'top-left', 'top-right', 'bottom-right', 'bottom-left'.
 */
function initPopperInstance(hotspot, popover, positioning) {
  popperInstance = createPopper(hotspot, popover, {
    placement: popperPositioning[positioning].placement,
    modifiers: [
      {
        name: 'offset',
        options: { offset: popperPositioning[positioning].offset, }
      },
      {
        name: 'flip',
        options: { boundary: tourStopsWrapper, },
      },
    ],
  });
}

/**
 * Destroys the Popper instance. Adheres to Popper.js' method/documentation
 */
function destroyPopperInstance() {
  if (popperInstance) {
    popperInstance.destroy();
    popperInstance = null;
  }
}

/**
 * Shows a Popper instance. Also adds the data-show data attribute.
 * @param {HTMLElement} hotspot - The hotspot element to hook the popper to
 * @param {HTMLElement} popover - The popover element to show
 */
function showPopper(hotspot, popover) {
  popover.setAttribute('data-show', '');
  const positioning = popover.dataset.positioning;
  initPopperInstance(hotspot, popover, positioning);
}

/**
 * Hides a Popper instance. Also removes the data-show data attribute.
 * @param {HTMLElement} popover - The popover element to hide
 */
function hidePopper(popover) {
  popover.removeAttribute('data-show');
  destroyPopperInstance();
}

/**
 * Returns the popover element linked to the hotspot provided
 * @param {HTMLElement} hotspot - The hotspot element to search out its linked popover
 * @return {HTMLElement} The linked popover element
 */
function getHotspotLinkedPopover(hotspot) {
  return document.querySelector(`#${hotspot.dataset.linkedPopover}`);
}

/**
 * Returns a stop's mobile focal point
 * @param {HTMLElement} stop - The stop element to search out its set mobile focal point (set as a CSS Custom Property)
 * @return {string} The mobile vertical focal point, represented as a string, with the number and percentage sign (eg. "34%")
 */
function getStopMobileFocalPoint(stop) {
  return getComputedStyle(stop).getPropertyValue('--mobile-vert-focal-point');
}

/**
 * Removes all of the relevant state-related CSS classes and custom properties from ALL stops
 */
function removeAllStopActiveStates() {
  welcomeStop.classList.remove('stop-is-active');
  exploreStop.classList.remove('stop-is-active');
  endStop.classList.remove('stop-is-active');
  tourStops.forEach(stop => stop.classList.remove('stop-is-active'));
  crumbs.forEach(crumb => {
    crumb.classList.remove('is-active');
    crumb.parentNode.classList.remove('is-a-previous-crumb');
  });
  hotspotsWrapper.classList.remove('manual-include-mode');
  hotspots.forEach(hs => {
    // console.log(hs);
    hs.parentNode.classList.remove('is-a-previous-stop');
    hs.classList.remove('hidden', 'included');
    hs.parentNode.style.removeProperty("--override-pos-x");
    hs.parentNode.style.removeProperty("--override-pos-y");
    hs.closest('.gctour-hotspot-item').classList.remove('is-active');
  } );
  popovers.forEach(popover => { hidePopper(popover); popover.classList.remove('is-active'); });
}

/**
 * Goes to a stop based on the provided selector
 * @param {string} selector - The selector to use for stop navigation (eg. "#gctour-stop-1")
 */
function goToStop(selector) {
  // console.log('selector: ', selector);

  // check to make sure that the selector is valid
  if (!selector) {
    console.info('goToStop\'s selector is falsy. Returning false.');
    return false;
  }

  if (selector === '#welcome' || selector === '#explore' || selector === '#end') {
    goToFauxStop(selector);

    return true;
  }

  // check to make sure that the URL hash starts with #gctour-stop
  else if (!selector.startsWith('#gctour-stop')) {
    console.info('URL hash does not match the tour stop selector pattern. Going to Welcome Stop.');
    //location.hash = '#welcome';
    location.hash = initialStopHash;
    return false;
  }

  let previousStop = getCurrentStop();

  // get the new tour stop from the selector
  let newStop = document.querySelector(selector);
  // make sure the selector provided found an element
  if (!newStop) { console.info('Invalid Stop in URL. Returning false'); return false; }
  let newStopNum = newStop.id.split("gctour-stop-")[1];

  // get the new tour stop's related UI elements from the selector
  let newStopHotspot = document.querySelector(`.gctour-hotspot[href="${selector}"]`);
  let newStopCrumb = document.querySelector(`.gctour-stop_crumb[href="${selector}"]`);
  let newPopover = getHotspotLinkedPopover(newStopHotspot);
  // let newPopoverArrow = newPopover.querySelector("[data-popper-arrow]");

  // get the hotspots to include (or exclude) on this new stop
  let newHsIncludes = newStop.dataset.hsVisibilityInclude;
  newHsIncludes = (newHsIncludes) ? newHsIncludes.split(',') : null;
  // console.log("Include list: ", (newHsIncludes) ? newHsIncludes : "null");
  let newHsExcludes = newStop.dataset.hsVisibilityExclude;
  newHsExcludes = (newHsExcludes) ? newHsExcludes.split(',') : null;
  // console.log("Exclude list: ", (newHsExcludes) ? newHsExcludes : "null");

  // get the hotspots to reposition on this new stop
  let newHsRepositionList = newStop.dataset.hsRepositionList;
  // console.log("newHsRepositionList: ", newHsRepositionList);
  newHsRepositionList = (newHsRepositionList) ? newHsRepositionList.split(';') : null;
  // console.log("Reposition array: ", (newHsRepositionList) ? newHsRepositionList : "null");

  // set the background position and transform origin properties (via a custom property, so only mobile is targeted) on all stops, allowing CSS transitions to handle tweening from one stop to another
  let previousStopFocalPt = (previousStop) ? getStopMobileFocalPoint(previousStop) : null;
  let newStopFocalPt = getStopMobileFocalPoint(newStop);
  tourStops.forEach(stop => {
    stop.style.backgroundPositionY = newStopFocalPt;
    stop.style.transformOrigin = `50% ${newStopFocalPt}`;
    if (previousStop && previousStop.id !== 'welcome') { stop.style.removeProperty('--mobileTransitionDelay'); }
  });

  // hide all other tour stops
  removeAllStopActiveStates();

  // show the Crumbs
  exploreTextWrapper.classList.add('hidden');
  crumbsWrapper.classList.remove('hidden');

  // show and activate appropriate stop and related UI elements
  newStop.classList.add('stop-is-active');
  (newStopCrumb) ? newStopCrumb.classList.add('is-active') : "";
  (newStopHotspot) ? newStopHotspot.parentNode.classList.add('is-active') : "";
  if (newHsIncludes) {
    hotspotsWrapper.classList.add('manual-include-mode');
    newHsIncludes.forEach(id => {
      let stop = document.querySelector(`.gctour-stop-wrapper[data-chosen-stop-id="${id}"]`)
      // console.log("stop.id", stop.id);

      let hs = document.querySelector(`.gctour-hotspot[href="#${stop.id}"]`)
      // console.log(hs);
      hs.classList.add('included');
    });
  }
  if (newHsExcludes) {
    newHsExcludes.forEach(id => {
      let stop = document.querySelector(`.gctour-stop-wrapper[data-chosen-stop-id="${id}"]`)
      // console.log("stop.id", stop.id);

      // hide the chosen stop's hotspots
      // prevent the user from excluding its own hotspot
      if ( !stop.classList.contains('stop-is-active') ) {
        let hs = document.querySelector(`.gctour-hotspot[href="#${stop.id}"]`)
        // console.log(hs);
        hs.classList.add('hidden');
      }
    });
  }
  if (newHsRepositionList) {
    newHsRepositionList.forEach(hs => {
      let hsObj = JSON.parse(hs);
      // console.log('hotspot reposition object: ', hsObj);

      let stop = document.querySelector(`.gctour-stop-wrapper[data-chosen-stop-id="${hsObj.stopId}"]`)
      // console.log(stop);

      // reposition the chosen hotspots
      // prevent the user from repositioning its own hotspot
      if ( !stop.classList.contains('stop-is-active') ) {
        let hs = document.querySelector(`.gctour-hotspot[href="#${stop.id}"]`)
        // console.log(hs);
        hs.parentNode.style.setProperty("--override-pos-x", hsObj.newPosX);
        hs.parentNode.style.setProperty("--override-pos-y", hsObj.newPosY);
      }
    });
  }
  showPopper(newStopHotspot, newPopover);
  newPopover.classList.add('is-active');
  tourStopsWrapper.style.setProperty("--active-stop", newStopNum);

  // visually idicate the hotspots of previous spots
  let newStopPreviousStops = getPreviousSiblings(newStopHotspot.parentNode, '.gctour-hotspot-item');
  // console.log(newStopHotspot);
  if (newStopPreviousStops) {
    newStopPreviousStops.forEach((el) => {
      el.classList.add('is-a-previous-stop');
    });
  }

  // visually idicate the hotspots of previous crumbs
  let previousCrumbs = getPreviousSiblings(newStopCrumb.parentNode, '.gctour-stop_crumb-item');
  // console.log("previousCrumbs: ", previousCrumbs);
  if (previousCrumbs) {
    previousCrumbs.forEach((el) => {
      el.classList.add('is-a-previous-crumb');
    });
  }

  // backdrop
  updateBackdropCoordinates(newStop);
  tourUiBackdrop.classList.add('is-active');
}

/**
 * Goes to a "Faux Stop" (eg. Welcome, Explore, End) based on the provided selector
 * @param {string} selector - The selector to use for stop navigation (eg. "#welcome")
 */
function goToFauxStop(selector) {
  // console.log(`welcome, explore, or end selector found -- going to the ${selector} panel`);
  removeAllStopActiveStates();
  let newStop = document.querySelector(selector);

  if (selector === '#welcome') {
    welcomeStop.classList.add('stop-is-active');
    exploreTextWrapper.classList.add('hidden');
    crumbsWrapper.classList.add('hidden');
    updateBackdropCoordinates(newStop);
    tourUiBackdrop.classList.add('is-active');
    hotspots.forEach(hs => { hs.classList.add('hidden'); });
    showPopper(welcomeStop, welcomePopover);

    tourStops.forEach(stop => {
      stop.style.backgroundPositionY = `50%`;
      stop.style.transformOrigin = `50% 50%`;
    });
    firstStop.style.setProperty("--mobileTransitionDelay", "1s");
  }
  else if (selector === '#explore') {
    if ( checkIfIsMobileWidth() ) {
      console.info('The viewport is at a mobile width. Going to welcome, as there is no Explore stop for mobile widths');
      location.hash = "#welcome";
      return true;
    }

    // show the first stop's hotspots, screenshot, etc.
    goToStop('#gctour-stop-1');
    // remove the active popover
    popovers.forEach(popover => { hidePopper(popover); });
    // remove the hotspot active class
    hotspots.forEach(hs => { hs.parentNode.classList.remove('is-active'); });

    // show Explore specific UI
    exploreStop.classList.add('stop-is-active');
    exploreTextWrapper.classList.remove('hidden');
    crumbsWrapper.classList.add('hidden');
    tourUiBackdrop.classList.remove('is-active');
  }
  else if (selector === '#end') {
    endStop.classList.add('stop-is-active');
    exploreTextWrapper.classList.add('hidden');
    crumbsWrapper.classList.remove('hidden');
    updateBackdropCoordinates(newStop);
    tourUiBackdrop.classList.add('is-active');
    hotspots.forEach((el) => { el.parentNode.classList.add('is-a-previous-stop'); });
    showPopper(endStop, endPopover);
  }
}

/**
 * Gets the currently active Stop
 * @return {HTMLElement} The currently active Stop
 */
function getCurrentStop() {
  return document.querySelector('.stop-is-active');
}

/**
 * Gets the previous Stop, referring to the currently active Stop
 * @return {String} The previous Stop's selector
 */
function getPrevStopSelector() {
  let currentStop = getCurrentStop();
  let currentStopNum = parseInt(currentStop.id.split('gctour-stop-')[1], 10); // gctour-stop-3
  if(currentStop.id === 'end') { currentStopNum = tourStops.length + 1; }
  // console.log(currentStopNum);
  let nextStopNum = (currentStopNum === 1) ? false : currentStopNum - 1;
  //if(!nextStopNum) { return `#welcome`; }
  if(!nextStopNum) { console.log("getPrevStopSelector", initialStopHash); return initialStopHash; }
  return (nextStopNum) ? `#gctour-stop-${nextStopNum}` : false;
}

/**
 * Gets the next Stop, referring to the currently active Stop
 * @return {String} The next Stop's selector
 */
function getNextStopSelector() {
  let currentStop = getCurrentStop();
  // console.log(currentStop);
  let currentStopNum = parseInt(currentStop.id.split('gctour-stop-')[1], 10);
  if(currentStop.id === 'welcome' || currentStop.id === 'explore') { currentStopNum = 0; }
  // console.log(currentStopNum);
  let nextStopNum = (currentStopNum === tourStops.length) ? false : currentStopNum + 1;
  if(!nextStopNum) { return `#end`; }
  return (nextStopNum) ? `#gctour-stop-${nextStopNum}` : false;
}

/**
 * Handler for hash change Stop navigation
 */
function handleHashChangeStopNavigation() {
  // console.log('handleHashChangeStopNavigation run. hash: ', location.hash);
  goToStop(location.hash);
}

/**
 * Updates the backdrop's relevant coordinate CSS Custom Properties
 * @param {HTMLElement} stop - The stop element to refer to
 */
function updateBackdropCoordinates(stop) {
  // since the admin's interactive active area selection tool can reverse the start/stop positions of both x & y coordinates, we need to find which point is lower 
  let clipXStart = Number(stop.dataset.backdropClipXStart.replace('%', ''));
  let clipXEnd = Number(stop.dataset.backdropClipXEnd.replace('%', ''));
  let clipYStart = Number(stop.dataset.backdropClipYStart.replace('%', ''));
  let clipYEnd = Number(stop.dataset.backdropClipYEnd.replace('%', ''));

  let clipXLower = (clipXStart < clipXEnd) ? clipXStart + '%' : clipXEnd + '%';
  let clipXHigher = (clipXStart > clipXEnd) ? clipXStart + '%' : clipXEnd + '%';
  let clipYLower = (clipYStart < clipYEnd) ? clipYStart + '%' : clipYEnd + '%';
  let clipYHigher = (clipYStart > clipYEnd) ? clipYStart + '%' : clipYEnd + '%';

  root.style.setProperty("--backdrop-clip-x-start", clipXLower);
  root.style.setProperty("--backdrop-clip-x-end", clipXHigher);
  root.style.setProperty("--backdrop-clip-y-start", clipYLower);
  root.style.setProperty("--backdrop-clip-y-end", clipYHigher);

  let activeAreaWidth = Math.abs(clipXEnd - clipXStart);
  // console.log('active area width: ', activeAreaWidth);
  root.style.setProperty("--backdrop-active-area-width", activeAreaWidth);
  let activeAreaHeight = Math.abs(clipYEnd - clipYStart);
  // console.log('active area height: ', activeAreaHeight);
  root.style.setProperty("--backdrop-active-area-height", activeAreaHeight);
}

/**
 * Checks if the window is at or below the defined mobile width
 * @return {boolean} True if at mobile width
 */
function checkIfIsMobileWidth() {
  return window.matchMedia(`(max-width: ${mobileMaxWidthBp})`).matches;
}

/**
 * Event handler for window resize. Debounces the handleExploreStopAtMobileWidth function.
 */
function handleResize() {
  debounce(handleExploreStopAtMobileWidth());
}

/**
 * If at the Explore stop, and the window width changes to mobile, redirect the location hash to an appropriate location.
 */
function handleExploreStopAtMobileWidth() {
  if (location.hash === "#explore" && checkIfIsMobileWidth()) {
    console.info('The resized viewport is at a mobile width. Going to welcome, as there is no Explore stop for mobile widths');
    location.hash = "#welcome";
    return true;
  }
}

/**
 * Handle Embed sizing
 */
function handleEmbedSizing() {
  let height = tourElement.offsetHeight;
  // console.log("Embed Sizing", height, window.frameElement);
  window.frameElement.style.height = height + "px";
  return true;
}


// Event Listeners & inits
// ---------------------------------------
window.addEventListener('keydown', (event) => {
  // Escape Key closes the poppers
  // --------------------------
  if ( (popperInstance) && (event.key === "Escape" || event.key === "Esc") ) {
    hidePopper(popperInstance.state.elements.popper);
  }

  // Use keyboard arrow keys for navigation
  // --------------------------
  else if ( event.key === "ArrowRight" ) {
    // console.log('right arrow key entered');
    let selector = getNextStopSelector();
    location.hash = selector;
    // goToStop(selector);
  }
  else if ( event.key === "ArrowLeft" ) {
    // console.log('Left arrow key entered');
    let selector = getPrevStopSelector();
    location.hash = selector;
    // goToStop(selector);
  }
});


// Window hash change
// --------------------------
window.onhashchange = handleHashChangeStopNavigation;

window.addEventListener('click', (evt) => {
  if ( evt.target.tagName.toLowerCase() === 'a' ) {
    let href = evt.target.getAttribute("href");

    // if a hash'd href
    if (href.substring(0, 1) === '#') {
      // prevents scrolling
      evt.preventDefault();
      // think of this as a "softer" way of adjusting the hash. It allows us to cancel the event default, which causes the browser to jump / scroll to the tour (bad if embedded in an iframe). Note that setting location.hash = href; manually here would still scroll the browser in Chrome.
      history.replaceState(null, '', href);
    }
  }

  // Popover dismiss buttons
  // --------------------------
  if ( evt.target.closest('.dismiss-button') ) {
    let popover = evt.target.closest('.popover');
    hidePopper(popover);
  }

  // Condition for when the popover has been closed/dismissed, but the user is still requesting that the current stop be shown (again). This is necessary because the app mostly runs on watching for changes to the location.hash, but in this case, the hash does not change
  // --------------------------
  else if (
    evt.target.closest('[href]') && 
    !evt.target.closest('[href]').classList.contains('dismiss-button') &&
    (evt.target.closest('[href]').getAttribute("href") === location.hash)
  ) {
    // console.log('link clicked, and its href is the same as the currently active stop')
    goToStop(location.hash);
  }

  // if the backdrop is clicked, close the popover & backdrop
  else if ( (evt.target === tourUiBackdrop) ) {
    if (popperInstance) {
      console.log("currentStop", getCurrentStop());
      hidePopper(popperInstance.state.elements.popper);
    }
    if(location.hash === "#welcome") {
      goToStop("#gctour-stop-1");
    }
    tourUiBackdrop.classList.remove('is-active');
  }
});

// Window resize
// --------------------------
window.addEventListener('resize', handleResize);


// Location Hash Inits
// --------------------------
if (
  // if the location hash matches a Faux Stop or the URL/hash pattern for the Stops
  location.hash === "#welcome" ||
  location.hash === "#explore" ||
  location.hash.startsWith("#gctour-stop-") ||
  location.hash === "#end"
  ) {
  goToStop(location.hash);
}
else if ( location.hash === "" ) {
  // go to the Welcome stop if no hash is defined
  console.info('The URL hash is empty. Going to Welcome.')
  //location.hash = "#gctour-stop-1";
  //location.hash = "#welcome";
  location.hash = initialStopHash;
  console.log(initialStopHash);
}
else {
  // A non-matching hash is used in the URL
  console.info('The URL hash does not match any of the available options. Going to Welcome.')
  //location.hash = "#gctour-stop-1";
  //location.hash = "#welcome";
  location.hash = initialStopHash;
}


// Inits if embedded in an iframe
// --------------------------
if ( window.frameElement ) {
  // since we're in an iframe, grab the parent document
  let parentDocument = windowParent.document;
  // grab all tour iframes on the page
  let tourIframes = parentDocument.querySelectorAll('.tour_frame-wrapper > iframe');

  // method for revealing / unhiding the iframe -- necessary to mitigate an issue where loading the iframe to a hash URL would scroll the browser (Chrome) down to the iframe on the page. There are next to no clearly documented fixes for this issue; the below is a modified take on a reported fix. The modification in this case was to add a setTimeout timer to the mix; timer values of 1ms and 12ms were tried, but both resulted in _occasional_ scrolls down to the iframe on hard refreshes. 120ms seems to be a sweetspot in initial testing (~70 hard refreshes @ different intervals) -- no scrolls down. Wacky, but it works... <3 KB
  function iframeRevealMethod(tourIframe) {
    setTimeout(() => {
      tourIframe.style.display = "block";
      // now that the iframe is showing, the container needs to be dynamically sized
      handleEmbedSizing();
    }, 120);
  }

  tourIframes.forEach((tourIframe) => {
    tourIframe.addEventListener('load', () => { iframeRevealMethod(tourIframe) });
  });

  windowParent.addEventListener('resize', function() {
    debounce(handleEmbedSizing());
  });
}
